import { ANNOUNCEMENT_SWATCHES } from '@accounts/shared';
import type { AnnouncementSwatch } from '@accounts/shared';
import type { ComponentPropsWithoutRef, CSSProperties, ReactNode } from 'react';

import { STR } from '../i18n';

import { cardHeaderStyle, cardStyle, tileLabelStyle, tileStyle } from './styles';

/**
 * The shared vocabulary of the demos — chips, status dots, callouts, cards, tiles,
 * skeletons. Every colour comes from a token-built class (`.c-*`, `.call-*`, `.dot-*`,
 * `.tx-*`) so all four palette x mode combinations follow automatically.
 */

/** Chip families from the token file. Named by intent, not by hue. */
export type ChipTone =
  | 'neutral'
  | 'positive'
  | 'admin'
  | 'attention'
  | 'danger'
  /**
   * Something that has RUN ITS COURSE — an announcement past its end time. Deliberately its
   * own tone rather than `danger`: an ended announcement is not a fault and must not wear
   * the strong danger red. The design of record names the soft red exactly (`c-rose`).
   */
  | 'expired'
  | 'planned';

const CHIP_CLASS: Record<ChipTone, string> = {
  neutral: 'c-slate',
  positive: 'c-emerald',
  admin: 'c-indigo',
  attention: 'c-amber',
  danger: 'c-red',
  expired: 'c-rose',
  planned: 'c-violet',
};

const CHIP_BASE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 999,
  borderWidth: 1,
  borderStyle: 'solid',
};

const CHIP_SIZE: Record<'sm' | 'md', CSSProperties> = {
  sm: { padding: '1px 7px', fontSize: 10, fontWeight: 700, flex: 'none' },
  md: { padding: '3px 9px', fontSize: 11, fontWeight: 650 },
};

/**
 * ONE chip, two ways of naming its colour, and no mapping layer between them.
 *
 * `tone` is the app's semantic vocabulary (positive, danger, …) and stays the default.
 * `swatch` is an OPERATOR-ASSIGNED colour — an announcement type's colour, or an
 * application's from the announcement-colour settings — and each swatch value IS the class
 * suffix of a chip family already defined in styles.css (`.c-amber` … `.c-slate`), themed
 * across all four palette x mode combinations. So the stored value renders directly:
 * `c-${swatch}`. There is deliberately nothing here that translates a swatch into a tone —
 * a translation table would be a second home for a fact the server already stores.
 *
 * When `swatch` is given it wins; when it is absent (or null — an unassigned application)
 * the chip falls back to `tone`, which is how "no colour assigned" renders as neutral
 * rather than as a blank or a missing pill.
 */
export function Chip({
  tone,
  swatch,
  size = 'md',
  style,
  children,
  ...rest
}: {
  tone: ChipTone;
  swatch?: AnnouncementSwatch | null;
  size?: 'sm' | 'md';
  style?: CSSProperties;
  children: ReactNode;
} & Omit<ComponentPropsWithoutRef<'span'>, 'className' | 'style' | 'children'>) {
  const className = swatch === undefined || swatch === null ? CHIP_CLASS[tone] : `c-${swatch}`;
  return (
    <span className={className} style={{ ...CHIP_BASE, ...CHIP_SIZE[size], ...style }} {...rest}>
      {children}
    </span>
  );
}

/**
 * THE COLOUR STRIP an operator picks a swatch from — the announcement type form and the
 * announcement-colour settings card, which draw the IDENTICAL strip by rule.
 *
 * The set is `ANNOUNCEMENT_SWATCHES`, imported from the shared contract and rendered in
 * that exact order: /web never spells the list out, so the server's enum and this control
 * cannot drift. Each value IS the class suffix that paints it (`c-amber` … `c-slate`), so
 * a button wears `swx c-${swatch}` and there is no mapping layer anywhere. There is
 * deliberately no free colour picker in this app.
 */
type SwatchStripBase = {
  /** The swatch currently chosen, or null when nothing is assigned. */
  value: AnnouncementSwatch | null;
  /** The group's accessible name — always says WHAT is being colored. */
  label: string;
  disabled?: boolean;
};

/**
 * NULLABILITY IS A DISCRIMINATED PROP PAIR, not a boolean plus a widened handler, so the
 * asymmetry between the two call sites is enforced by the type system rather than by a
 * comment somebody can miss. Opt in (`nullable`) and the handler MUST accept null; leave it
 * off — the default, and what every existing caller does by omission — and the handler is
 * only ever handed a real swatch, so a non-nullable surface cannot receive a null it has no
 * meaning for.
 */
type SwatchStripProps = SwatchStripBase &
  (
    | { nullable: true; onPick: (swatch: AnnouncementSwatch | null) => void }
    | { nullable?: false; onPick: (swatch: AnnouncementSwatch) => void }
  );

export function SwatchStrip({ value, onPick, label, disabled = false, ...rest }: SwatchStripProps) {
  const nullable = rest.nullable === true;

  return (
    <div role="group" aria-label={label} style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {nullable ? (
        /*
         * "No color", FIRST in the strip and selectable exactly like any other swatch —
         * the ruled shape. Null is a legitimate saved value (the pill simply renders
         * uncolored), so it gets a visible slot rather than being reachable only by never
         * having chosen. It wears no `c-*` class, which is what makes it read as empty;
         * `.swx` already draws the outline, and `.swx[aria-pressed="true"]` gives it the
         * IDENTICAL selected treatment every other swatch gets.
         */
        <button
          type="button"
          className="swx swx-none"
          aria-pressed={value === null}
          aria-label={STR.swatchNone}
          title={STR.swatchNone}
          disabled={disabled}
          onClick={() => {
            (onPick as (swatch: AnnouncementSwatch | null) => void)(null);
          }}
        />
      ) : null}
      {ANNOUNCEMENT_SWATCHES.map((swatch) => (
        <button
          key={swatch}
          type="button"
          className={`swx c-${swatch}`}
          aria-pressed={value === swatch}
          aria-label={STR.swatchNames[swatch]}
          title={STR.swatchNames[swatch]}
          disabled={disabled}
          onClick={() => {
            onPick(swatch);
          }}
        />
      ))}
    </div>
  );
}

/**
 * R3 — the health vocabulary. `neutral` is the "never checked" state and is deliberately
 * distinct from `bad`: a registered application that has not reported yet is not a fault.
 */
export type DotTone = 'ok' | 'warn' | 'bad' | 'neutral';

export function Dot({ tone, size = 8 }: { tone: DotTone; size?: number }) {
  return (
    <span
      className={`dot-${tone}`}
      style={{ width: size, height: size, borderRadius: 999, flex: 'none' }}
      aria-hidden="true"
    />
  );
}

export type CalloutTone = 'neutral' | 'danger' | 'positive' | 'attention';

const CALLOUT_CLASS: Record<CalloutTone, string> = {
  neutral: 'call-slate',
  danger: 'call-red',
  positive: 'call-emerald',
  attention: 'call-orange',
};

export function Callout({
  tone,
  role,
  style,
  children,
}: {
  tone: CalloutTone;
  role?: 'alert' | 'status';
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div
      className={CALLOUT_CLASS[tone]}
      {...(role === undefined ? {} : { role })}
      style={{
        padding: '10px 13px',
        borderRadius: 9,
        borderWidth: 1,
        borderStyle: 'solid',
        fontSize: 12.5,
        lineHeight: 1.55,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Card({ style, children }: { style?: CSSProperties; children: ReactNode }) {
  return <div style={{ ...cardStyle, ...style }}>{children}</div>;
}

export function CardHeader({
  title,
  aside,
  action,
}: {
  title: ReactNode;
  aside?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div style={cardHeaderStyle}>
      <span>{title}</span>
      {aside}
      {action === undefined ? null : <div style={{ marginLeft: 'auto' }}>{action}</div>}
    </div>
  );
}

export function Tile({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={tileStyle}>
      <div className="tx-subtle" style={tileLabelStyle}>
        {label}
      </div>
      <div style={{ fontSize: 14.5, fontWeight: 600, marginTop: 6, lineHeight: 1.35 }}>
        {children}
      </div>
    </div>
  );
}

export function Skeleton({ style }: { style: CSSProperties }) {
  return <div className="skel" style={style} aria-hidden="true" />;
}

/** The genuine first-paint skeleton for a master/detail screen. */
export function MasterDetailSkeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '288px minmax(0,1fr)', flex: 1 }}>
      <div style={{ borderRight: '1px solid var(--border)', background: 'var(--surface)', padding: 12 }}>
        {[1, 2, 3, 4, 5].map((row) => (
          <div key={row} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 10px' }}>
            <Skeleton style={{ height: 8, width: 8, borderRadius: 999, flex: 'none' }} />
            <Skeleton style={{ height: 11, width: 130, borderRadius: 5 }} />
          </div>
        ))}
      </div>
      <div style={{ padding: 26 }}>
        <Skeleton style={{ height: 18, width: 200, borderRadius: 6 }} />
        <Skeleton style={{ height: 11, width: 260, borderRadius: 5, marginTop: 12 }} />
        <Skeleton style={{ height: 120, width: '100%', borderRadius: 12, marginTop: 22 }} />
      </div>
    </div>
  );
}

export function EmptyScreen({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div style={{ padding: '80px 40px', textAlign: 'center' }}>
      <div style={{ fontSize: 15.5, fontWeight: 650 }}>{title}</div>
      <p
        className="tx-subtle"
        style={{ maxWidth: 400, margin: '8px auto 18px', fontSize: 13, lineHeight: 1.6 }}
      >
        {body}
      </p>
      {action}
    </div>
  );
}

export function EmptyRow({ children }: { children: ReactNode }) {
  return (
    <div className="tx-subtle" style={{ padding: '32px 16px', textAlign: 'center', fontSize: 13 }}>
      {children}
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" aria-hidden="true" />;
}

/**
 * D3 — a truncated list must say so. Renders IFF the rows in hand fall short of the server's
 * own total for that query, so its ABSENCE is a statement too: what you see is all there is.
 * The rule lives here, once, rather than at each list — three copies of "is this list short?"
 * is three chances for one of them to drift into claiming a partial list is whole.
 *
 * It belongs at the FOOT of the list, which is where somebody scrolling meets the end of what
 * was loaded — a header notice is read before the question has been asked.
 */
export function ListTruncationNotice({
  loaded,
  total,
  message = STR.common.listTruncated,
}: {
  loaded: number;
  total: number | null;
  /**
   * The DEFAULT sentence describes the admin lists, whose search filters the loaded rows in
   * the browser. A surface whose filters are resolved server-side needs a different sentence
   * — but the same one rule about WHEN a list is short, which is why the wording is a
   * parameter rather than a second copy of this component.
   */
  message?: (loaded: number, total: number) => string;
}) {
  if (total === null || loaded >= total) return null;
  return (
    <div
      className="tx-muted"
      role="status"
      style={{
        padding: '11px 14px 14px',
        borderTop: '1px solid var(--border)',
        fontSize: 11.5,
        lineHeight: 1.5,
        textWrap: 'pretty',
      }}
    >
      {message(loaded, total)}
    </div>
  );
}
